import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Charity } from '@/types/charity';

export const useCharity = (ownerId?: string | null) => {
  const [charity, setCharity] = useState<Charity | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!ownerId) {
      setIsLoading(false);
      return;
    }

    const fetchCharity = async () => {
      try {
        setIsLoading(true);
        
        // Check session before making API call
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.error('Session error in useCharity:', sessionError);
          setError(new Error('Authentication session error'));
          return;
        }

        if (!session) {
          console.warn('No active session in useCharity');
          setCharity(null);
          return;
        }

        const { data, error } = await supabase
          .from('charities')
          .select('*')
          .eq('owner_id', ownerId)
          .single();

        if (error && error.code !== 'PGRST116') {
          // PGRST116 is "no rows returned" - not an error for our purposes
          
          // Handle specific error types - 406 often means RLS blocking access (no charity exists)
          if (error.message?.includes('406') || error.message?.includes('Not Acceptable') || error.code === '406') {
            console.log('No charity access for user (RLS policies) - treating as no charity exists');
            setCharity(null);
            return;
          }
          
          throw error;
        }

        setCharity(data || null);
      } catch (err) {
        console.error('Error fetching charity:', err);
        
        // Handle different error types
        if (err instanceof Error) {
          if (err.message.includes('406') || err.message.includes('Not Acceptable')) {
            console.log('Charity 406 error in catch block - treating as no charity exists');
            setCharity(null);
            return;
          } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
            setError(new Error('Authentication required. Please log in again.'));
          } else {
            setError(err);
          }
        } else {
          setError(new Error('Unknown error occurred'));
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchCharity();
  }, [ownerId]);

  return { charity, isLoading, error, setCharity };
};
